/**
 * Standard uniforms and helpers injected ahead of every visualization fragment shader. This is
 * the contract a visualization author writes against (the ISF loader will map onto it later).
 */
import { MAX_VIZ_PARAMS } from '../../../shared/params';
import { NUM_BANDS } from '../../../shared/audioFrame';

export const PRELUDE = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform vec2  uResolution;   // render-target size in pixels
uniform float uTime;         // seconds since start
uniform float uDt;           // seconds since last frame

uniform float uBands[${NUM_BANDS}]; // 0..1, kick → air, smoothed
uniform float uBass, uMid, uTreble, uLevel, uCentroid;
uniform float uOnset;        // 0..1 onset strength
uniform float uBeatPhase;    // 0..1 within the beat (predicted, latency-compensated)
uniform float uBarPhase;     // 0..1 within the bar
uniform float uBeatPulse;    // 0..1 envelope that peaks on each beat
uniform float uPhrasePhase;  // 0..1 within the phrase (16 bars by default)
uniform float uBeats;        // running beat count (beat index + phase): for beat-stepped patterns
uniform float uBeatInBar;    // 0..3
uniform float uBpm, uConfidence;
uniform float uEnergy;       // 0..1 song energy (slow)
uniform float uBuildup;      // 0..1 build-up progress
uniform float uPitch;        // melody pitch as a MIDI note (60 = middle C), held while unsure
uniform float uPitchConf;    // 0..1 how clearly a melody pitch is heard
uniform float uChroma[12];   // 0..1 per pitch class, C … B
uniform vec2  uPitchHist[128]; // recent melody, newest first: (pitch, confidence) every 25 ms

uniform float uIntensity;    // always 1 here: master intensity is applied after compositing
uniform float uBeatReact;    // how strongly motion follows the beat
uniform float uP[${MAX_VIZ_PARAMS}]; // visualization parameters, in manifest order

uniform sampler2D uSpectrum; // 128×1, log-frequency, 0..1
uniform sampler2D uWaveform; // 512×1, −1..1
uniform sampler2D uPalette;  // 256×1 gradient
uniform sampler2D uSpectrogram; // 128×64 history of the spectrum; row uSpectrogramRow is newest
uniform float uSpectrogramRow;
uniform float uPaletteShift;
uniform float uPaletteCyclic; // 1 when the palette wraps smoothly

out vec4 fragColor;

const float PI = 3.14159265359;
const float TAU = 6.28318530718;

// Cyclic palettes wrap; others ping-pong so out-of-range values never jump to the opposite end.
vec3 palette(float t) {
  float x = t + uPaletteShift;
  x = uPaletteCyclic > 0.5 ? fract(x) : 1.0 - abs(fract(x * 0.5) * 2.0 - 1.0);
  return texture(uPalette, vec2(x, 0.5)).rgb;
}
vec3 paletteClamped(float t) { return texture(uPalette, vec2(clamp(t + uPaletteShift, 0.0, 1.0), 0.5)).rgb; }
float spectrum(float x) { return texture(uSpectrum, vec2(clamp(x, 0.0, 1.0), 0.5)).r; }
float waveform(float x) { return texture(uWaveform, vec2(clamp(x, 0.0, 1.0), 0.5)).r; }
// Spectrum history: age 0 = now, 1 = ~1 s ago (64 rendered frames).
float spectrogram(float x, float age) {
  float row = mod(uSpectrogramRow - clamp(age, 0.0, 1.0) * 63.0 + 64.0, 64.0);
  return texture(uSpectrogram, vec2(clamp(x, 0.0, 1.0), (row + 0.5) / 64.0)).r;
}
// Rainbow colour for a pitch: the 12 notes go once round the colour wheel (C = red).
vec3 noteColour(float midi) {
  float h = fract(midi / 12.0);
  return clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
}
float pulse() { return uBeatPulse * uBeatReact; }
float hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }

float noise2(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), u.x),
             mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise2(p); p = p * 2.03 + vec2(17.1, 9.3); a *= 0.5; }
  return v;
}

// A laser beam from origin o along unit direction d: a hot core plus a soft scatter glow, widening
// slightly with distance. Units are screen heights.
float beam(vec2 p, vec2 o, vec2 d, float width) {
  vec2 v = p - o;
  float t = dot(v, d);
  if (t < 0.0) return 0.0;
  float dist = length(v - d * t);
  float w = width * (1.0 + t * 0.2);
  // core + near scatter + broad volumetric bloom (what makes beams read as being "in haze")
  return exp(-dist * dist / (w * w)) + 0.22 * exp(-dist / (w * 7.0)) + 0.06 * exp(-dist / (w * 40.0));
}

// Drifting haze: 1 = clear air, lower/higher = thinner/thicker smoke. amount 0..1.5.
float hazeAt(vec2 p, float amount) {
  float h = fbm(p * 4.5 + vec2(uTime * 0.06, uTime * 0.025));
  h = fbm(p * 2.0 + h * 1.5 - vec2(uTime * 0.03, 0.0)); // domain warp: rolling, smoky shapes
  return mix(1.0, 0.1 + 2.4 * h * h, clamp(amount / 1.5, 0.0, 1.0));
}
`;
